import React, { ReactNode, useCallback, useMemo, useState, useEffect } from 'react'
import append from 'ramda/es/append'
import takeLast from 'ramda/es/takeLast'

import ErrorsContext, { Context, ErrorsQueue } from './errors-context'


type Props = Readonly<{
  children: ReactNode
}>

const ErrorsProvider = ({ children }: Props) => {
  const [errors, setErrors] = useState<ErrorsQueue>([])

  const [reset, setReset] = useState<boolean>(false)
  const [timeoutError, setTimeoutError] = useState<boolean>(false)

  useEffect(() => {
    setReset(false)
  }, [reset])


  // добавление ошибки в состояние
  const handleThrowError = useCallback((text: string, response?: Response, params?: any) => {
    setErrors(append({
      time: Date.now(),
      text,
      ...(response ? { response } : {}),
      ...(params ? { params } : {})
    }))
  }, [])


  // ошибка таймаута, можно вывести сообщение об отсутствии интернета
  const handleTimeoutError = useCallback(() => {
    setTimeoutError(true)
  }, [])


  // очистка всех ошибок
  const handleClearErrors = useCallback(() => {
    setErrors([])
  }, [])


  // "применение" ошибок, можно отсылать их на сервер...
  // покв удаляются все кроме одной
  const handleApplyErrors = useCallback(() => {
    if (errors.length > 1) {
      setErrors(takeLast(1))
    }
  }, [errors, setErrors])


  // перезагрузка приложения
  const handleResetApp = useCallback(() => {
    setTimeoutError(false)
    setReset(true)
  }, [setTimeoutError, setReset])


  // сброс ошибки о таймауте соединения/недоступности данных
  const handleResetTimeoutError = useCallback(() => {
    setTimeoutError(false)
  }, [])


  // раз в интервал производятся какие то действия над ошибками,
  // их количество в состоянии при этом уменьшается
  useEffect(() => {
    const intervalId = setInterval(handleApplyErrors, 60000)
    return () => clearInterval(intervalId)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps


  const memoizedValue: Context = useMemo(() => ([errors, {
    throwError: handleThrowError,
    clearErrors: handleClearErrors,
    resetApp: handleResetApp,
    throwTimeoutError: handleTimeoutError,
    resetTimeoutError: handleResetTimeoutError,
  }, timeoutError]), [
    errors,
    handleThrowError,
    handleClearErrors,
    handleResetApp,
    handleTimeoutError,
    timeoutError,
    handleResetTimeoutError
  ])


  return (
    <ErrorsContext.Provider value={memoizedValue}>
      {reset ? null : children}
    </ErrorsContext.Provider>
  )
}

export default ErrorsProvider
